import { forwardRef, type ButtonHTMLAttributes, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';

export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /** Estado do toggle (controlado). */
  checked: boolean;
  /** Chamado com o novo valor quando o usuário muda. */
  onCheckedChange: (next: boolean) => void;
  /** Label legível — exibido só para screen readers (`aria-label`) caso
   * não haja `<Label>` associado. */
  srLabel?: string;
}

/**
 * Toggle on/off baseado em `role="switch"`. Mantém contrato ARIA
 * (`aria-checked`, foco visível). Tap-target ≥ 44px no mobile via padding
 * implícito (h-6 do trilho + toque no botão inteiro). Sem depender de
 * Radix — implementação leve o suficiente pra ser apenas um `<button>`.
 *
 * @example
 * <Switch checked={on} onCheckedChange={setOn} srLabel="Ativar módulo" />
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, srLabel, className, disabled, ...props },
  ref,
): ReactElement {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={srLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full',
        'border-2 border-transparent transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-input',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
});
